import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Dialog as D } from 'radix-ui';
import { ArrowRight, Box, Building2, FileKey, FileText, FolderKanban, KeyRound, LifeBuoy, Receipt, Search, Server, User, UserRound } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/app/auth';
import { NAV } from '@/app/nav';
import { Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useModuleEnabled } from '@/modules/subscription/api';

interface Hit {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  href: string;
}

const ICONS: Record<string, ReactNode> = {
  organization: <Building2 />,
  contact: <User />,
  ticket: <LifeBuoy />,
  configuration: <Server />,
  password: <KeyRound />,
  vault_key: <FileKey />,
  document: <FileText />,
  flexible_asset: <Box />,
  employee: <UserRound />,
  invoice: <Receipt />,
  project: <FolderKanban />,
};
const GROUPS: Record<string, string> = {
  organization: 'Organizations',
  contact: 'Contacts',
  ticket: 'Tickets',
  configuration: 'Configurations',
  password: 'Passwords',
  vault_key: 'Keys & certificates',
  document: 'Documents',
  flexible_asset: 'Assets',
  employee: 'People',
  invoice: 'Invoices',
  project: 'Projects',
};

/** ⌘K: jump anywhere. Searches the API as you type; with no query it lists pages. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const moduleEnabled = useModuleEnabled(); // Phase I (I-B, I2): hide modules the plan leaves out

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);
  // Clear the query when the palette closes (adjusting state during render, not in an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) setQ('');
  }

  const search = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => api<{ results: Hit[] }>('/search', { query: { q: debounced }, signal }),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const go = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };
  const pages = NAV.flatMap((g) => g.items).filter((i) => (!i.permission || can(i.permission)) && (!i.needsEmployee || user?.employeeId) && moduleEnabled(i.planModule));
  const groups = (search.data?.results ?? []).reduce<Record<string, Hit[]>>((acc, h) => {
    (acc[h.type] ??= []).push(h);
    return acc;
  }, {});

  const item = 'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm data-[selected=true]:bg-surface-hover [&_svg]:size-4 [&_svg]:text-subtle';
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-slate-950/40" />
        <D.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-surface shadow-pop">
          <D.Title className="sr-only">Search</D.Title>
          <D.Description className="sr-only">Search across Denwen MSP or jump to a page</D.Description>
          <Command shouldFilter={!debounced} label="Search">
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="size-4 text-subtle" />
              <Command.Input value={q} onValueChange={setQ} autoFocus placeholder="Search or jump to…" className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-subtle" />
              {search.isFetching ? <Spinner className="text-subtle" /> : null}
            </div>
            <Command.List className="max-h-[min(60vh,calc(88dvh-6rem))] overflow-y-auto overscroll-contain p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted">{debounced.length >= 2 && !search.isFetching ? 'No results' : 'Type at least two characters'}</Command.Empty>
              {debounced.length < 2 ? (
                <Command.Group heading="Go to" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-subtle">
                  {pages.map((p) => (
                    <Command.Item key={p.to} value={p.label} onSelect={() => go(p.to)} className={item}>
                      <p.icon />
                      {p.label}
                      <ArrowRight className="ml-auto opacity-0 [[data-selected=true]_&]:opacity-100" />
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : (
                Object.entries(groups).map(([type, hits]) => (
                  <Command.Group key={type} heading={GROUPS[type] ?? type} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-subtle">
                    {hits.map((h) => (
                      <Command.Item key={`${h.type}-${h.id}`} value={`${h.type}-${h.id}`} onSelect={() => go(h.href)} className={item}>
                        {ICONS[h.type] ?? <ArrowRight />}
                        <span className="min-w-0 flex-1 truncate">{h.title}</span>
                        {h.subtitle ? <span className="max-w-[45%] truncate text-xs text-subtle">{h.subtitle}</span> : null}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))
              )}
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
